import { QwikUCard, Name, Phone, Email, Address, Organization, Website, SocialMedia, VCardOptions } from '@/types';

function vCardEncode(value: string | undefined): string {
  if (!value) return '';
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

function nameToVCard(name: Name): string[] {
  if (!name) return [];

  const lines: string[] = [];
  const components = [
    name.last || '',
    name.first || '',
    name.middle || '',
    name.prefix || '',
    name.suffix || '',
  ];

  // Add formatted name (FN) - required in vCard 4.0
  const formattedName = [name.prefix, name.first, name.middle, name.last, name.suffix]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (formattedName) {
    lines.push(`FN:${vCardEncode(formattedName)}`);
  }

  // Add structured name (N)
  lines.push(`N:${components.map(vCardEncode).join(';')}`);

  return lines;
}

function phoneToVCard(phone: Phone): string[] {
  if (!phone?.number) return [];

  const v4 = false;
  let s = v4 ? "TEL;VALUE=uri" : "TEL";
  const types = new Set<string>();

  // Handle phone types - use Android-compatible type names
  switch (phone.label?.toLowerCase()) {
    case "home": types.add("HOME"); break;
    case "work": types.add("WORK"); break;
    case "faxwork": types.add("FAX"); types.add("WORK"); break;
    case "faxhome": types.add("FAX"); types.add("HOME"); break;
    case "mobile": types.add("CELL"); break;
    case "main": types.add("MAIN"); break;
    case "other": types.add("VOICE"); break;
    default: types.add("VOICE"); // Default type for Android compatibility
  }

  if (!v4 && phone.isPrimary) {
    types.add("PREF");
  }

  // Add type parameters - use uppercase for better Android compatibility
  const typeArray = [...types];
  if (typeArray.length === 1) {
    s += `;TYPE=${typeArray[0]}`;
  } else if (typeArray.length > 1) {
    s += v4 ? `;TYPE="${typeArray.join(',')}"` : `;TYPE=${typeArray.join(',')}`;
  }

  if (v4 && phone.isPrimary) {
    s += ";PREF=1";
  }

  // Add phone number - ensure proper formatting for Android
  s += v4 ? `:tel:${vCardEncode(phone.number)}` : `:${vCardEncode(phone.number)}`;
  return [s];
}

function emailToVCard(email: Email): string[] {
  if (!email?.address) return [];

  const v4 = false;
  let s = "EMAIL";
  const types = new Set<string>();

  // Handle email types - use uppercase for Android compatibility
  switch (email.label?.toLowerCase()) {
    case "home": types.add("HOME"); break;
    case "work": types.add("WORK"); break;
    default: types.add("INTERNET");
  }

  if (email.isPrimary) {
    v4 ? s += ";PREF=1" : types.add("PREF");
  }

  // Add type parameters - use uppercase for better Android compatibility
  const typeArray = [...types];
  if (typeArray.length > 0) {
    s += `;TYPE=${typeArray.join(',')}`;
  }

  s += `:${vCardEncode(email.address)}`;
  return [s];
}

function addressToVCard(address: Address): string[] {
  if (!address) return [];

  const v4 = false;
  let s = "ADR";
  const types = new Set<string>();

  // Handle address types - use uppercase for Android compatibility
  switch (address.label?.toLowerCase()) {
    case "home": types.add("HOME"); break;
    case "work": types.add("WORK"); break;
    case "other": types.add("OTHER"); break;
  }

  // Add type parameters - use uppercase for better Android compatibility
  const typeArray = [...types];
  if (typeArray.length > 0) {
    s += `;TYPE=${typeArray.join(',')}`;
  }

  // Handle structured vs unstructured addresses
  if (address.street || address.city || address.state || address.postalCode) {
    s += `:${[
      
      '', // Extended address
      vCardEncode(address.street || ''),
      vCardEncode(address.city || ''),
      vCardEncode(address.state || ''),
      vCardEncode(address.postalCode || ''),
      vCardEncode(address.country || '')
    ].join(';')}`;
  } else if (address.address) {
    s += `:;;${vCardEncode(address.address)};;;;`;
  }

  return [s];
}

function organizationToVCard(organization: Organization): string[] {
  if (!organization) return [];

  const lines: string[] = [];
  if (organization.company) {
    lines.push(`ORG:${vCardEncode(organization.company)}`);
  }
  if (organization.title) {
    lines.push(`TITLE:${vCardEncode(organization.title)}`);
  }
  return lines;
}

function websiteToVCard(website: Website): string[] {
  if (!website?.url) return [];
  return [`URL${website.label ? `;TYPE=${website.label.toLowerCase()}` : ''}:${vCardEncode(website.url)}`];
}

function socialMediaToVCard(socialMedia: SocialMedia): string[] {
  if (!socialMedia?.userName) return [];

  const protocol = socialMedia.label === "custom" && socialMedia.customLabel
    ? socialMedia.customLabel
    : socialMedia.label;

  if (!protocol) return [];

  return [`X-SOCIALPROFILE;TYPE=${vCardEncode(protocol)}:${vCardEncode(socialMedia.userName)}`];
}

export async function fetchPhotoAsBase64(url: string): Promise<string | null> {
  if (!url) return null;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const blob = await response.blob();
    if (!blob.type.startsWith('image/')) {
      throw new Error('URL does not point to an image');
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Data = (reader.result as string).split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = () => reject(new Error('Failed to read image data'));
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error fetching photo:', error);
    return null;
  }
}

export function toVCard(
  mySmartcard: QwikUCard,
  myphotoByte: string | null,
  { withPhoto = false, productId = null, includeDate = false, encoding = 'base64' }: VCardOptions = {}
): string {
  if (!mySmartcard) {
    throw new Error('Contact information is required');
  }

  // Force vCard 3.0 for maximum compatibility with Android
  const v4 = false;
  const lines: string[] = [
    "BEGIN:VCARD",
    "VERSION:3.0",  // Version 3.0 has the best cross-device compatibility, especially for Android
    "PROFILE:VCARD"
  ];

  try {
    // Add optional fields
    if (productId) {
      lines.push(`PRODID:${vCardEncode(productId)}`);
    }

    if (includeDate) {
      lines.push(`REV:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z`);
    }

    // Add name fields - ensure FN is always present (required by spec and Android)
    if (mySmartcard.name) {
      const nameLines = nameToVCard(mySmartcard.name);
      // Ensure FN exists as it's required for both iOS and Android
      if (!nameLines.some(line => line.startsWith('FN:'))) {
        const fallbackName = mySmartcard.name.first || mySmartcard.name.last || 'Unknown';
        lines.push(`FN:${vCardEncode(fallbackName)}`);
      }
      lines.push(...nameLines);
    }

    // Add photo if available - with Android and iOS compatibility
    if (withPhoto && myphotoByte) {
      // Clean up the photo data
      let photoData = myphotoByte;
      if (photoData.includes('base64,')) {
        photoData = photoData.split('base64,')[1];
      }

      // Remove any whitespace, newlines, or special characters from base64
      photoData = photoData.replace(/[\r\n\s]/g, '');

      // Detect image type from base64 header
      const isJPEG = photoData.startsWith('/9j/');
      const isPNG = photoData.startsWith('iVBOR');

      // For maximum Android compatibility:
      // 1. Use JPEG;ENCODING=BASE64 format (not b)
      // 2. Ensure no line breaks in the data
      // 3. Use proper vCard 3.0 syntax
      const photoType = isJPEG ? 'JPEG' : (isPNG ? 'PNG' : 'JPEG');

      // Android prefers BASE64 encoding over 'b'
      const photoLine = `PHOTO;ENCODING=BASE64;TYPE=${photoType}:${photoData}`;

      // For Android compatibility, don't split the photo line
      // Android vCard parsers are very strict about line folding
      lines.push(photoLine);
    }

    // Add contact fields if they exist - ensure proper mobile formatting
    if (Array.isArray(mySmartcard.phones)) {
      const phones = mySmartcard.phones
        .map((phone: Phone) => {
          // Normalize phone numbers for better compatibility
          const normalizedNumber = phone.number.replace(/[^\d+]/g, '');
          return { ...phone, number: normalizedNumber };
        })
        .map(phoneToVCard)
        .flat();
      lines.push(...phones);
    }

    if (Array.isArray(mySmartcard.emails)) {
      lines.push(...mySmartcard.emails.map(emailToVCard).flat());
    }

    if (Array.isArray(mySmartcard.addresses)) {
      lines.push(...mySmartcard.addresses.map(addressToVCard).flat());
    }

    if (Array.isArray(mySmartcard.organizations)) {
      const orgs = mySmartcard.organizations.map(organizationToVCard).flat();
      // Ensure only one ORG field for better iOS compatibility
      const filteredOrgs = orgs.filter((org: string, index: number, array: string[]) =>
        org.startsWith('ORG:') ? array.findIndex((o: string) => o.startsWith('ORG:')) === index : true
      );
      lines.push(...filteredOrgs);
    }

    if (Array.isArray(mySmartcard.websites)) {
      lines.push(...mySmartcard.websites.map(websiteToVCard).flat());
    }

    if (Array.isArray(mySmartcard.socialMedias)) {
      lines.push(...mySmartcard.socialMedias.map(socialMediaToVCard).flat());
    }

    lines.push("END:VCARD");

    // Ensure proper line endings for all platforms
    // Use \r\n for maximum compatibility with Android
    // Also ensure no extra whitespace or BOM characters
    const vCardString = lines.join("\r\n") + "\r\n";

    // Remove any potential BOM characters and ensure proper encoding
    return vCardString.replace(/^\uFEFF/, '');
  } catch (error) {
    console.error('Error generating vCard:', error);
    throw new Error('Failed to generate vCard: ' + (error as Error).message);
  }
}

// Create a minimal vCard specifically for Android compatibility
export function toVCardAndroid(
  mySmartcard: QwikUCard,
  myphotoByte: string | null,
  { withPhoto = false, productId = null, includeDate = false }: VCardOptions = {}
): string {
  if (!mySmartcard) {
    throw new Error('Contact information is required');
  }

  // Create a minimal vCard for maximum Android compatibility
  const lines: string[] = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    "PROFILE:VCARD"
  ];

  try {
    // Add name - FN is required for Android
    if (mySmartcard.name) {
      const formattedName = [mySmartcard.name.prefix, mySmartcard.name.first, mySmartcard.name.middle, mySmartcard.name.last, mySmartcard.name.suffix]
        .filter(Boolean)
        .join(' ')
        .trim();

      if (formattedName) {
        lines.push(`FN:${vCardEncode(formattedName)}`);
      }

      // Add structured name
      const components = [
        mySmartcard.name.last || '',
        mySmartcard.name.first || '',
        mySmartcard.name.middle || '',
        mySmartcard.name.prefix || '',
        mySmartcard.name.suffix || '',
      ];
      lines.push(`N:${components.map(vCardEncode).join(';')}`);
    }

    // Add photo with Android-optimized encoding
    if (withPhoto && myphotoByte) {
      let photoData = myphotoByte;
      if (photoData.includes('base64,')) {
        photoData = photoData.split('base64,')[1];
      }
      photoData = photoData.replace(/[\r\n\s]/g, '');

      // For Android, use a smaller photo size and ensure it's JPEG
      // Android vCard parsers can be sensitive to large photos
      if (photoData.length > 50000) { // If photo is too large, skip it for Android
        console.warn('Photo too large for Android vCard, skipping photo');
      } else {
        // Use BASE64 encoding which Android prefers
        lines.push(`PHOTO;ENCODING=BASE64;TYPE=JPEG:${photoData}`);
      }
    }

    // Add essential contact info only - keep it simple for Android
    if (Array.isArray(mySmartcard.phones)) {
      mySmartcard.phones.forEach((phone: Phone) => {
        if (phone.number) {
          const normalizedNumber = phone.number.replace(/[^\d+]/g, '');
          const type = phone.label?.toLowerCase() === 'mobile' ? 'CELL' : 'VOICE';
          lines.push(`TEL;TYPE=${type}:${vCardEncode(normalizedNumber)}`);
        }
      });
    }

    if (Array.isArray(mySmartcard.emails)) {
      mySmartcard.emails.forEach((email: Email) => {
        if (email.address) {
          const type = email.label?.toLowerCase() === 'work' ? 'WORK' : 'HOME';
          lines.push(`EMAIL;TYPE=${type}:${vCardEncode(email.address)}`);
        }
      });
    }

    // Add minimal organization info for Android
    if (Array.isArray(mySmartcard.organizations) && mySmartcard.organizations.length > 0) {
      const org = mySmartcard.organizations[0]; // Only use first organization
      if (org.company) {
        lines.push(`ORG:${vCardEncode(org.company)}`);
      }
      if (org.title) {
        lines.push(`TITLE:${vCardEncode(org.title)}`);
      }
    }

    lines.push("END:VCARD");

    // Use \r\n line endings for Android compatibility
    // Ensure no extra spaces or characters
    const vCardString = lines.join("\r\n") + "\r\n";

    // Remove any potential BOM characters and ensure proper encoding
    return vCardString.replace(/^\uFEFF/, '');
  } catch (error) {
    console.error('Error generating Android vCard:', error);
    throw new Error('Failed to generate Android vCard: ' + (error as Error).message);
  }
}

// Create a minimal vCard without photos for problematic Android devices
export function toVCardAndroidMinimal(
  mySmartcard: QwikUCard,
  { productId = null, includeDate = false }: VCardOptions = {}
): string {
  if (!mySmartcard) {
    throw new Error('Contact information is required');
  }

  // Create the most minimal vCard possible for Android compatibility
  const lines: string[] = [
    "BEGIN:VCARD",
    "VERSION:3.0"
  ];

  try {
    // Add only essential name information
    if (mySmartcard.name) {
      const formattedName = [mySmartcard.name.first, mySmartcard.name.last]
        .filter(Boolean)
        .join(' ')
        .trim();

      if (formattedName) {
        lines.push(`FN:${vCardEncode(formattedName)}`);
      }

      // Add minimal structured name
      const components = [
        mySmartcard.name.last || '',
        mySmartcard.name.first || '',
        '', '', '' // Skip middle, prefix, suffix for minimal version
      ];
      lines.push(`N:${components.map(vCardEncode).join(';')}`);
    }

    // Add only phone numbers - most essential for contact cards
    if (Array.isArray(mySmartcard.phones)) {
      mySmartcard.phones.forEach((phone: Phone) => {
        if (phone.number) {
          const normalizedNumber = phone.number.replace(/[^\d+]/g, '');
          lines.push(`TEL:${vCardEncode(normalizedNumber)}`);
        }
      });
    }

    // Add only emails
    if (Array.isArray(mySmartcard.emails)) {
      mySmartcard.emails.forEach((email: Email) => {
        if (email.address) {
          lines.push(`EMAIL:${vCardEncode(email.address)}`);
        }
      });
    }

    lines.push("END:VCARD");

    // Use \r\n line endings for Android compatibility
    const vCardString = lines.join("\r\n") + "\r\n";

    // Remove any potential BOM characters and ensure proper encoding
    return vCardString.replace(/^\uFEFF/, '');
  } catch (error) {
    console.error('Error generating minimal Android vCard:', error);
    throw new Error('Failed to generate minimal Android vCard: ' + (error as Error).message);
  }
}

